// ─────────────────────────────────────────────────────────────────────────
// inspection-draft-sync.js
//
// Server mirror for InspectionPage drafts (per locked design — Feb 2026):
//   - Triggered on SUB-ZONE COMPLETION (not on every keystroke)
//   - Full payload sent so drafts can resume on any device
//   - 3-draft cap per user enforced server-side (409 on 4th new key)
//   - Soft "Continue here?" banner when a different device has a fresher copy
//   - Hydrates localStorage from server on Dashboard mount + 60s tick
//   - Final submit purges the server row (server.py handles that side)
// ─────────────────────────────────────────────────────────────────────────
import axios from 'axios';
import { draftKey, saveInspectionDraft } from './inspection-draft';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const api = axios.create({ baseURL: `${API_BASE}/api`, withCredentials: true });

const DEVICE_ID_KEY = 'rt_device_id_v1';

export function getDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

// Push a draft to the server. Returns:
//   { ok: true, doc } on success
//   { ok: false, conflict: true, existing: [...] } on 409 (3-draft cap hit)
//   { ok: false, error: <message> } on network/other errors
export async function pushDraftToServer({
  userId, stationId, inspectionType, payload, summary, force = false,
}) {
  if (!userId || !stationId) return { ok: false, error: 'missing_ids' };
  const body = {
    user_id: userId,
    station_id: stationId,
    inspection_type: inspectionType || 'individual',
    payload: payload || {},
    summary: summary || {},
    device_id: getDeviceId(),
    version: 1,
    force,
  };
  try {
    const r = await api.put('/inspection-drafts', body);
    return { ok: true, doc: r.data };
  } catch (e) {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail;
    if (status === 409 && detail?.code === 'draft_limit_exceeded') {
      return { ok: false, conflict: true, existing: detail.existing || [], limit: detail.limit };
    }
    return { ok: false, error: e?.message || 'sync_failed' };
  }
}

// Fetch the current user's server drafts. Returns array or [] on error.
export async function fetchUserDrafts(userId) {
  if (!userId) return [];
  try {
    const r = await api.get('/inspection-drafts', { params: { user_id: userId } });
    return Array.isArray(r.data) ? r.data : [];
  } catch (e) {
    console.warn('[DraftSync] fetchUserDrafts failed', e?.message);
    return [];
  }
}

// Hydrate localStorage from server (LWW by updated_at).
//   - If server has a key local doesn't → pull it down to localStorage.
//   - If both have the same key → server wins when server.updated_at > local.saved_at.
//   - If local has a key server doesn't → leave alone (will be pushed up next time
//     a sub-zone is completed).
export async function hydrateFromServer(userId) {
  if (!userId) return { pulled: 0 };
  const serverDrafts = await fetchUserDrafts(userId);
  if (serverDrafts.length === 0) return { pulled: 0 };

  let pulled = 0;
  for (const s of serverDrafts) {
    const p = s.payload || {};
    // Reconstruct the localStorage-shape draft so the existing Resume Card
    // code path keeps working without any change.
    const local = {
      ...p,
      v: 2,
      user_id: s.user_id,
      station_id: s.station_id,
      inspection_type: s.inspection_type,
      saved_at: new Date(s.updated_at).getTime() || Date.now(),
      started_at: p.started_at || (new Date(s.created_at).getTime() || Date.now()),
    };

    // Compare with what's currently in localStorage for this key.
    let existingMap = {};
    try {
      existingMap = JSON.parse(localStorage.getItem('inspection_drafts_v2') || '{}');
    } catch (_) { /* noop */ }
    const k = draftKey(s.user_id, s.station_id, s.inspection_type);
    const existing = existingMap[k];
    if (!existing || (existing.saved_at || 0) < (local.saved_at || 0)) {
      // Use the canonical saver so its event fires + listeners refresh.
      saveInspectionDraft({
        ...local,
        selectedStation: s.station_id,
        inspectionType: s.inspection_type,
      }, { userId });
      pulled += 1;
    }
  }
  return { pulled };
}

// Delete a server draft by composite key. Idempotent.
export async function deleteServerDraft({ userId, stationId, inspectionType }) {
  if (!userId || !stationId) return { ok: false };
  const k = draftKey(userId, stationId, inspectionType);
  try {
    await api.delete(`/inspection-drafts/by-key/${encodeURIComponent(k)}`, {
      params: { user_id: userId },
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e?.message };
  }
}
